import type { FabProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// NEW VARIANT
declare module '@mui/material/Fab' {
  interface FabPropsVariantOverrides {
    outlined: true;
    outlinedExtended: true;
    soft: true;
    softExtended: true;
  }
}

const Fab = (theme: Theme) => {
  const isLight = theme.palette.mode === 'light';

  const rootStyle = (ownerState: FabProps) => {
    const defaultColor = ownerState.color === 'default';

    const inheritColor = ownerState.color === 'inherit';

    const circularVariant = ownerState.variant === 'circular';

    const extendedVariant = ownerState.variant === 'extended';

    const outlinedVariant = ownerState.variant === 'outlined';

    const outlinedExtendedVariant = ownerState.variant === 'outlinedExtended';

    const softVariant = ownerState.variant === 'soft';

    const softExtendedVariant = ownerState.variant === 'softExtended';

    const defaultStyle = {
      '&:hover, &:active': {
        boxShadow: 'none',
      },
      ...((circularVariant || extendedVariant) && {
        ...((defaultColor || inheritColor) && {
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
          boxShadow: theme.customShadows.z8,
          color: theme.palette.grey[800],
        }),
        ...(inheritColor && {
          ...(!isLight && {
            '&:hover': {
              backgroundColor: theme.palette.grey[700],
            },
            backgroundColor: theme.palette.grey[800],
            color: 'inherit',
          }),
        }),
      }),

      ...((outlinedVariant || outlinedExtendedVariant) && {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        ...((defaultColor || inheritColor) && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          border: `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
        }),
        ...(defaultColor && {
          ...(!isLight && {
            color: theme.palette.text.secondary,
          }),
        }),
      }),

      ...((softVariant || softExtendedVariant) && {
        boxShadow: 'none',
        ...(defaultColor && {
          '&:hover': {
            backgroundColor: theme.palette.grey[400],
          },
          backgroundColor: theme.palette.grey[300],
          color: theme.palette.grey[800],
        }),
        ...(inheritColor && {
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.24),
          },
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
        }),
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        ...((circularVariant || extendedVariant) && {
          '&:hover': {
            backgroundColor: theme.palette[color].dark,
          },
          boxShadow: theme.customShadows[color],
        }),
        ...((outlinedVariant || outlinedExtendedVariant) && {
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.08),
            border: `solid 1px ${theme.palette[color].main}`,
          },
          border: `solid 1px ${alpha(theme.palette[color].main, 0.48)}`,
          color: theme.palette[color].main,
        }),
        ...((softVariant || softExtendedVariant) && {
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.32),
          },
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          color: theme.palette[color][isLight ? 'dark' : 'light'],
        }),
      }),
    }));

    const disabledState = {
      '&.Mui-disabled': {
        ...((outlinedVariant || outlinedExtendedVariant) && {
          backgroundColor: 'transparent',
          border: `solid 1px ${theme.palette.action.disabledBackground}`,
        }),
      },
    };

    const size = {
      ...((extendedVariant ||
        outlinedExtendedVariant ||
        softExtendedVariant) && {
        '& svg': {
          marginRight: theme.spacing(1),
        },
        width: 'auto',
        ...(ownerState.size === 'small' && {
          borderRadius: 17,
          height: 34,
          minHeight: 34,
          padding: theme.spacing(0, 1),
        }),
        ...(ownerState.size === 'medium' && {
          borderRadius: 20,
          height: 40,
          minHeight: 40,
          padding: theme.spacing(0, 2),
        }),
        ...(ownerState.size === 'large' && {
          borderRadius: 24,
          height: 48,
          minHeight: 48,
          padding: theme.spacing(0, 2),
        }),
      }),
    };

    return [...colorStyle, defaultStyle, disabledState, size];
  };

  return {
    MuiFab: {
      defaultProps: {
        color: 'primary',
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: FabProps }) =>
          rootStyle(ownerState),
      },
    },
  };
};

export default Fab;
